'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/cn';

export const alertVariants = cva(
  'relative w-full rounded-xl border px-3.5 py-3 text-[12.5px] [&>svg]:absolute [&>svg]:top-3 [&>svg]:left-3.5 [&>svg]:size-7.5 [&>svg]:rounded-md [&>svg]:p-1.75 [&>svg~*]:pl-10.5',
  {
    variants: {
      variant: {
        info: 'bg-info-tint/50 text-foreground border-info/25 [&>svg]:bg-info-tint [&>svg]:text-info',
        success:
          'bg-success-tint/50 text-foreground border-success/25 [&>svg]:bg-success-tint [&>svg]:text-success',
        warning:
          'bg-warning-tint/45 text-foreground border-warning/30 [&>svg]:bg-warning-tint [&>svg]:text-warning-strong',
        error:
          'bg-destructive-tint/55 text-foreground border-destructive/25 [&>svg]:bg-destructive-tint [&>svg]:text-destructive-strong',
        neutral:
          'bg-surface text-foreground border-border [&>svg]:bg-muted [&>svg]:text-muted-foreground',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
);

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, ...props }, ref) => (
    <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
  ),
);
Alert.displayName = 'Alert';

export const AlertTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('text-[12.5px] leading-snug font-[655] tracking-[-0.01em]', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

export const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-muted-foreground mt-0.5 text-[11.5px] [&_p]:leading-normal', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';
